import React, { useState } from 'react';
import { View, StyleSheet, Text, TextInput, Pressable } from 'react-native-web';
import Modal from 'react-modal';
import Button from './Button';
import { loadStripe } from '@stripe/stripe-js';
import {
    EmbeddedCheckout,
    EmbeddedCheckoutProvider,
} from '@stripe/react-stripe-js';
import { getStripeCheckout, depositFunds } from '../services/BusinessService';
import toast from 'react-hot-toast';

const stripePromise = loadStripe((import.meta.env.VITE_REACT_APP_STRIPE_PK || process.env.REACT_APP_STRIPE_PK));

const DepositModal = ({ isOpen, onClose, balance, onSuccess }) => {
    const [depositAmount, setDepositAmount] = useState(0);
    const [ccFee, setCCFee] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);
    const [clientSecret, setClientSecret] = useState("");
    const [showCheckout, setShowCheckout] = useState(false);
    const [showPaymentSelection, setShowPaymentSelection] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [useCardOnFile, setUseCardOnFile] = useState(false);

    function closeModal() {
        setShowCheckout(false);
        setShowPaymentSelection(false);
        setDepositAmount(0);
        setCCFee(0);
        setTotalAmount(0);
        setClientSecret("");
        setPaymentMethod(null);
        setUseCardOnFile(false);
        onClose();
    }

    function updateDepositAmount(e) {
        setDepositAmount(e.target.value);
        setCCFee((parseFloat(e.target.value) * .0295 + .3).toFixed(2));
        setTotalAmount((parseFloat(e.target.value) + parseFloat(e.target.value) * .0295 + .3).toFixed(2));
    }

    function retrieveCheckout() {
        if (!totalAmount || totalAmount < 0) {
            return;
        } else {
            let req = {
                amount: parseFloat(totalAmount),
            };
            getStripeCheckout(req)
                .then(resp => {
                    if (resp.status === 200 && resp.data && resp.data.clientSecret) {
                        setClientSecret(resp.data.clientSecret);

                        // Check if payment method exists (card on file)
                        if (resp.data.paymentMethod && resp.data.paymentMethod.card) {
                            setPaymentMethod(resp.data.paymentMethod);
                            setShowPaymentSelection(true);
                        } else {
                            // No card on file, go directly to checkout
                            setShowCheckout(true);
                        }
                    }
                })
        }
    }

    function handleUseCardOnFile() {
        if (!paymentMethod || !paymentMethod.id) {
            toast.error('Payment method not found');
            return;
        }

        const req = {
            amount: parseFloat(totalAmount),
            paymentMethodId: paymentMethod.id,
        };

        // Show loading toast
        const loadingToast = toast.loading('Processing deposit...');

        depositFunds(req)
            .then(resp => {
                toast.dismiss(loadingToast);

                if (resp && resp.status === 200) {
                    toast.success(`Successfully deposited $${totalAmount}!`);
                    closeModal();

                    // Call onSuccess callback to refresh balance
                    if (onSuccess) {
                        onSuccess();
                    }
                } else {
                    toast.error('Deposit failed. Please try again.');
                }
            })
            .catch(err => {
                toast.dismiss(loadingToast);
                console.error('Deposit error:', err);
                toast.error('Deposit failed. Please try again.');
            });
    }

    function handleUseNewCard() {
        setUseCardOnFile(false);
        setShowPaymentSelection(false);
        setShowCheckout(true);
    }

    function goBackToPaymentSelection() {
        setShowCheckout(false);
        setShowPaymentSelection(true);
    }

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={closeModal}
            style={styles.modal}
        >
            {!showCheckout && !showPaymentSelection && (
                <View style={styles.modalContainer}>
                    <View style={styles.modalHeader}>
                        <Text style={styles.modalTitle}>Deposit Funds</Text>
                        <Pressable 
                            onPress={closeModal} 
                            style={({ hovered }) => [
                                styles.closeButton,
                                hovered && styles.closeButtonHovered
                            ]}
                            accessible={true}
                            accessibilityLabel="Close modal"
                            accessibilityRole="button"
                        >
                            <Text style={styles.closeButtonText}>×</Text>
                        </Pressable>
                    </View>
                    <View style={styles.modalBody}>
                        <Text style={styles.modalDescription}>Deposit funds to pay out to creators</Text>
                        <View style={styles.depositSection}>
                            <View style={styles.depositRowStyled}>
                                <Text style={styles.depositLabelStyled}>Deposit Amount</Text>
                                <View style={styles.inputContainer}>
                                    <Text style={styles.currencyPrefix}>$</Text>
                                    <TextInput 
                                        style={styles.depositInputStyled} 
                                        value={depositAmount} 
                                        onChange={updateDepositAmount}
                                        placeholder="0.00"
                                        placeholderTextColor="#9ca3af"
                                        keyboardType="numeric"
                                    />
                                </View>
                            </View>
                            <View style={styles.feeBreakdown}>
                                <View style={styles.feeRow}>
                                    <Text style={styles.feeLabel}>Processing Fee</Text>
                                    <Text style={styles.feeValue}>${ccFee}</Text>
                                </View>
                                <View style={styles.divider} />
                                <View style={styles.totalRow}>
                                    <Text style={styles.totalLabel}>Total Amount</Text>
                                    <Text style={styles.totalValue}>${totalAmount}</Text>
                                </View>
                            </View>
                        </View>
                    </View>
                    <View style={styles.modalFooter}>
                        <Button text="Cancel" onPress={closeModal} variant='secondary' style={styles.modalButton} />
                        <Button text="Continue to Payment" onPress={retrieveCheckout} style={styles.modalButton}/>
                    </View>
                </View>
            )}
            {showPaymentSelection && paymentMethod && (
                <View style={styles.modalContainer}>
                    <View style={styles.modalHeader}>
                        <Text style={styles.modalTitle}>Choose Payment Method</Text>
                        <Pressable
                            onPress={closeModal}
                            style={({ hovered }) => [
                                styles.closeButton,
                                hovered && styles.closeButtonHovered
                            ]}
                            accessible={true}
                            accessibilityLabel="Close modal"
                            accessibilityRole="button"
                        >
                            <Text style={styles.closeButtonText}>×</Text>
                        </Pressable>
                    </View>
                    <View style={styles.modalBody}>
                        <Text style={styles.modalDescription}>
                            Select how you'd like to pay ${totalAmount}
                        </Text>

                        {/* Card on File Option */}
                        <Pressable
                            style={({ hovered }) => [
                                styles.paymentOptionCard,
                                hovered && styles.paymentOptionCardHovered
                            ]}
                            onPress={handleUseCardOnFile}
                            accessible={true}
                            accessibilityLabel={`Use card ending in ${paymentMethod.card.last4}`}
                            accessibilityRole="button"
                        >
                            <View style={styles.paymentOptionContent}>
                                <View style={styles.cardIcon}>
                                    <Text style={styles.cardIconText}>💳</Text>
                                </View>
                                <View style={styles.paymentOptionInfo}>
                                    <Text style={styles.paymentOptionTitle}>Card on File</Text>
                                    <Text style={styles.paymentOptionDescription}>
                                        {paymentMethod.card.brand.toUpperCase()} •••• {paymentMethod.card.last4}
                                    </Text>
                                    <Text style={styles.paymentOptionExpiry}>
                                        Expires {paymentMethod.card.exp_month}/{paymentMethod.card.exp_year}
                                    </Text>
                                </View>
                                <View style={styles.arrowIcon}>
                                    <Text style={styles.arrowIconText}>→</Text>
                                </View>
                            </View>
                        </Pressable>

                        {/* New Card Option */}
                        <Pressable
                            style={({ hovered }) => [
                                styles.paymentOptionCard,
                                hovered && styles.paymentOptionCardHovered
                            ]}
                            onPress={handleUseNewCard}
                            accessible={true}
                            accessibilityLabel="Enter a new card"
                            accessibilityRole="button"
                        >
                            <View style={styles.paymentOptionContent}>
                                <View style={styles.cardIcon}>
                                    <Text style={styles.cardIconText}>+</Text>
                                </View>
                                <View style={styles.paymentOptionInfo}>
                                    <Text style={styles.paymentOptionTitle}>Use a Different Card</Text>
                                    <Text style={styles.paymentOptionDescription}>
                                        Enter new card details
                                    </Text>
                                </View>
                                <View style={styles.arrowIcon}>
                                    <Text style={styles.arrowIconText}>→</Text>
                                </View>
                            </View>
                        </Pressable>
                    </View>
                </View>
            )}
            {showCheckout && (
                <View style={styles.modalContainer}>
                    <View style={styles.modalHeader}>
                        <Text style={styles.modalTitle}>Complete Payment</Text>
                        <Pressable
                            onPress={closeModal}
                            style={({ hovered }) => [
                                styles.closeButton,
                                hovered && styles.closeButtonHovered
                            ]}
                            accessible={true}
                            accessibilityLabel="Close modal"
                            accessibilityRole="button"
                        >
                            <Text style={styles.closeButtonText}>×</Text>
                        </Pressable>
                    </View>
                    <View style={styles.modalBody}>
                        {paymentMethod && showPaymentSelection === false && (
                            <Pressable
                                style={styles.backButton}
                                onPress={goBackToPaymentSelection}
                                accessible={true}
                                accessibilityLabel="Change payment method"
                                accessibilityRole="button"
                            >
                                <Text style={styles.backButtonText}>← Change Payment Method</Text>
                            </Pressable>
                        )}
                        <View style={styles.checkoutContainer}>
                            <EmbeddedCheckoutProvider
                                stripe={stripePromise}
                                options={{clientSecret}}
                            >
                                <EmbeddedCheckout />
                            </EmbeddedCheckoutProvider>
                        </View>
                    </View>
                </View>
            )}
        </Modal>
    );
};

const styles = StyleSheet.create({
    modal: {
        overlay: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(57, 109, 224, .8)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000,
        },
        content: {
            position: 'relative',
            top: 'auto',
            left: 'auto',
            right: 'auto',
            bottom: 'auto',
            margin: 'auto',
            maxHeight: '80vh',
            background: 'none',
            border: 'none',
            borderRadius: 0,
            padding: 0,
            overflow: 'visible',
            width: 'auto',
            maxWidth: '90vw',
        }
    },
    modalContainer: {
        backgroundColor: 'white',
        borderRadius: 12,
        overflow: 'hidden',
        width: '100%',
        maxWidth: 500,
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.1,
        shadowRadius: 20,
        elevation: 5,
    },
    modalHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 25,
        borderBottomWidth: 1,
        borderBottomColor: '#e5e7eb',
    },
    modalTitle: {
        fontFamily: "'forma-djr-display', sans-serif",
        fontSize: 24,
        fontWeight: 'bold',
        color: '#396de0',
    },
    closeButton: {
        width: 32,
        height: 32,
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 16,
        backgroundColor: '#f3f4f6',
        transition: 'all 0.2s ease',
    },
    closeButtonHovered: {
        backgroundColor: '#e5e7eb',
        transform: [{ scale: 1.1 }],
    },
    closeButtonText: {
        fontSize: 24,
        color: '#6b7280',
        lineHeight: 24,
    },
    modalBody: {
        padding: 25,
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
    },
    modalDescription: {
        fontFamily: "'lato', sans-serif",
        fontSize: 16,
        color: '#6b7280',
        marginBottom: 25,
        textAlign: 'center',
    },
    depositSection: {
        backgroundColor: '#f9fafb',
        borderRadius: 8,
        padding: 20,
        borderWidth: 1,
        borderColor: '#e5e7eb',
    },
    depositRowStyled: {
        marginBottom: 20,
    },
    depositLabelStyled: {
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
        fontWeight: '600',
        color: '#374151',
        marginBottom: 8,
    },
    inputContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: 'white',
        borderWidth: 1,
        borderColor: '#d1d5db',
        borderRadius: 8,
        paddingHorizontal: 12,
        transition: 'border-color 0.2s ease',
    },
    currencyPrefix: {
        fontFamily: "'lato', sans-serif",
        fontSize: 16,
        color: '#6b7280',
        marginRight: 4,
    },
    depositInputStyled: {
        flex: 1,
        fontSize: 16,
        fontFamily: "'lato', sans-serif",
        paddingVertical: 12,
        outlineStyle: 'none',
        border: 'none',
        backgroundColor: 'transparent',
    },
    feeBreakdown: {
        backgroundColor: 'white',
        borderRadius: 6,
        padding: 16,
        borderWidth: 1,
        borderColor: '#e5e7eb',
    },
    feeRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
    },
    feeLabel: {
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
        color: '#6b7280',
    },
    feeValue: {
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
        color: '#6b7280',
        fontWeight: '500',
    },
    divider: {
        height: 1,
        backgroundColor: '#e5e7eb',
        marginVertical: 12,
    },
    totalRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    totalLabel: {
        fontFamily: "'lato', sans-serif",
        fontSize: 16,
        fontWeight: '600',
        color: '#111827',
    },
    totalValue: {
        fontFamily: "'lato', sans-serif",
        fontSize: 18,
        fontWeight: 'bold',
        color: '#111827',
    },
    modalFooter: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 25,
        borderTopWidth: 1,
        borderTopColor: '#e5e7eb',
        gap: 12,
    },
    modalButton: {
        minWidth: 120,
    },
    checkoutContainer: {
        minHeight: 400,
    },
    paymentOptionCard: {
        backgroundColor: 'white',
        borderWidth: 2,
        borderColor: '#e5e7eb',
        borderRadius: 12,
        padding: 20,
        marginBottom: 16,
        transition: 'all 0.2s ease',
        cursor: 'pointer',
    },
    paymentOptionCardHovered: {
        borderColor: '#396de0',
        backgroundColor: '#f9fafb',
        transform: [{ scale: 1.02 }],
        boxShadow: '0 4px 12px rgba(57, 109, 224, 0.15)',
    },
    paymentOptionContent: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    cardIcon: {
        width: 56,
        height: 56,
        borderRadius: 28,
        backgroundColor: '#e6f2ff',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 16,
    },
    cardIconText: {
        fontSize: 28,
    },
    paymentOptionInfo: {
        flex: 1,
    },
    paymentOptionTitle: {
        fontFamily: "'lato', sans-serif",
        fontSize: 18,
        fontWeight: '600',
        color: '#111827',
        marginBottom: 4,
    },
    paymentOptionDescription: {
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
        color: '#6b7280',
        marginBottom: 2,
    },
    paymentOptionExpiry: {
        fontFamily: "'lato', sans-serif",
        fontSize: 12,
        color: '#9ca3af',
    },
    arrowIcon: {
        marginLeft: 12,
    },
    arrowIconText: {
        fontSize: 24,
        color: '#396de0',
        fontWeight: 'bold',
    },
    backButton: {
        marginBottom: 16,
        padding: 8,
        alignSelf: 'flex-start',
    },
    backButtonText: {
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
        color: '#396de0',
        fontWeight: '600',
    },
});

export default DepositModal;